<script>
	import { onMount } from 'svelte';
	import NavBarReusable from './NavbarReusable.svelte';

	import AdminRescheduleRequestsComponent from './AdminRescheduleRequestsComponent.svelte';
	import AdminClassRegistrationRequestsComponent from './AdminClassRegistrationRequestsComponent.svelte';
	import AdminRegistrationComponent from './AdminRegistrationComponent.svelte';
	import SelectUsersSMSComponent from './SelectUsersSMSComponent.svelte';
	import CreateInformationSectionComponent from './CreateInformationSectionComponent.svelte';
	import InformationSectionsDisplayComponent from './InformationSectionsDisplayComponent.svelte';
	import BalanceTransactionsCsvDownloader from './BalanceTransactionsCsvDownloader.svelte';
	import TeacherRevenuePaymentCsvDownloader from './TeacherRevenuePaymentCsvDownloader.svelte';
	import TeachersListComponent from './TeachersListComponent.svelte';
	import StudentsListComponent from './StudentsListComponent.svelte';
	import AdminFirstClassesNewTeachersComponent from './AdminFirstClassesNewTeachersComponent.svelte';
	import AdminClassAttendanceComponent from './AdminClassAttendanceComponent.svelte';
	import AdminDashboardLauncher from './AdminDashboardLauncher.svelte';
	import AdminAgreements from './AdminAgreements.svelte';

	const collapsedKey = 'adminAllDashboard:collapsed';

	const widgetOrder = /** @type {const} */ ([
		'agreements',
		'activity',
		'monthlyFeedback',
		'classAttendance',
		'rescheduleRequests',
		'classRegistrationRequests',
		'classRegistrationCreate',
		'smsRecipients',
		'informationSectionsSubmit',
		'informationSectionsDisplay',
		'teachersList',
		'studentsList',
		'firstClassesNewTeachers',
		'balanceCsv',
		'teacherRevenueCsv',
	]);

	let collapsed = {
		agreements: true,
		activity: false,
		monthlyFeedback: false,
		classAttendance: false,
		rescheduleRequests: false,
		classRegistrationRequests: false,
		classRegistrationCreate: true,
		smsRecipients: true,
		informationSectionsSubmit: true,
		informationSectionsDisplay: true,
		teachersList: true,
		studentsList: true,
		firstClassesNewTeachers: true,
		balanceCsv: true,
		teacherRevenueCsv: true,
	};

	let refresh = {
		activity: 0,
		monthlyFeedback: 0,
		classAttendance: 0,
		rescheduleRequests: 0,
		classRegistrationRequests: 0,
		classRegistrationCreate: 0,
		smsRecipients: 0,
		informationSectionsSubmit: 0,
		informationSectionsDisplay: 0,
		teachersList: 0,
		studentsList: 0,
		firstClassesNewTeachers: 0,
		balanceCsv: 0,
		teacherRevenueCsv: 0,
	};

	function widgetTitle(key) {
		switch (key) {
			case 'agreements': return 'Dokumentai pasirašymui';
			case 'activity':
				return '📊 Activity Dashboard';
			case 'monthlyFeedback':
				return 'Monthly email review';
			case 'classAttendance':
				return 'Class Attendance';
			case 'rescheduleRequests':
				return 'Reschedule Requests';
			case 'classRegistrationRequests':
				return 'Class Registration Requests';
			case 'classRegistrationCreate':
				return 'Create Class Registration';
			case 'smsRecipients':
				return 'Send Bulk SMS';
			case 'informationSectionsSubmit':
				return 'Bendra Informacija SUBMIT';
			case 'informationSectionsDisplay':
				return 'Bendra Informacija DISPLAY';
			case 'teachersList':
				return 'Teachers List';
			case 'studentsList':
				return 'Students List';
			case 'firstClassesNewTeachers':
				return 'First Classes with New Teachers';
			case 'balanceCsv':
				return '📥 Balance CSV';
			case 'teacherRevenueCsv':
				return '💰 Teacher Revenue CSV';
			default:
				return key;
		}
	}

	function normalizeCollapsedState() {
		const next = {};
		for (const k of widgetOrder) next[k] = !!collapsed?.[k];
		collapsed = next;
		saveCollapsedState();
	}

	function loadCollapsedState() {
		try {
			const raw = localStorage.getItem(collapsedKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== 'object') return;
			const next = {};
			for (const k of widgetOrder) next[k] = !!parsed[k];
			collapsed = next;
		} catch {
			// ignore
		}
	}

	function saveCollapsedState() {
		try {
			localStorage.setItem(collapsedKey, JSON.stringify(collapsed));
		} catch {
			// ignore
		}
	}

	function openWidget(key) {
		collapsed = { ...collapsed, [key]: false };
		saveCollapsedState();
	}

	function closeWidget(key) {
		collapsed = { ...collapsed, [key]: true };
		saveCollapsedState();
	}

	function toggleCollapsed(key) {
		collapsed = { ...collapsed, [key]: !collapsed[key] };
		saveCollapsedState();
	}

	function closeAllWidgets() {
		const next = {};
		for (const k of widgetOrder) next[k] = true;
		collapsed = next;
		saveCollapsedState();
	}

	function reloadWidget(key) {
		refresh = { ...refresh, [key]: (refresh[key] || 0) + 1 };
	}

	$: openWidgetKeys = widgetOrder.filter(k => !collapsed[k]);

	onMount(() => {
		loadCollapsedState();
		normalizeCollapsedState();
	});
</script>

<div class="app-container">
	<NavBarReusable label="Admin" />

	<div class="page">
		<section class="section">
			<div class="section-title-row">
				<h2 class="section-title">Admin dashboard</h2>
				<button type="button" class="btn small" on:click={closeAllWidgets} disabled={openWidgetKeys.length === 0}>
					Close all widgets
				</button>
			</div>

			{#if widgetOrder.some(k => collapsed[k])}
				<div class="widget-dock" aria-label="Closed widgets">
					<div class="widget-dock-label">Closed widgets</div>
					<div class="widget-dock-buttons">
						{#each widgetOrder as key (key)}
							{#if collapsed[key]}
								<button type="button" class="dock-btn" on:click={() => openWidget(key)}>
									{widgetTitle(key)}
								</button>
							{/if}
						{/each}
					</div>
				</div>
			{/if}

			<div class="widgets-grid">
				{#if openWidgetKeys.length === 0}
					<div class="empty widgets-empty">Select a widget above to open.</div>
				{:else}
					{#each openWidgetKeys as key (key)}
						{#if key === 'agreements'}
							<div class="card widget" id="adminall-widget-agreements">
								<div class="widget-header"><button type="button" class="btn small" on:click={() => closeWidget('agreements')}>Uždaryti dokumentus</button></div>
								<div class="widget-body"><AdminAgreements /></div>
							</div>
						{/if}
						{#if key === 'monthlyFeedback'}
							<AdminDashboardLauncher
								title="Monthly email review"
								href="#/admin/monthly-feedback"
								description="Review each family's frozen teacher feedback email, hold anything that needs attention, then send eligible emails from the full review page."
								launchLabel="Launch Monthly email review"
								on:close={() => closeWidget('monthlyFeedback')}
							/>
						{/if}
						{#if key === 'activity'}
							<div class="card widget activity-card" id="adminall-widget-activity">
								<div class="widget-header">
									<div class="widget-title">
										<span class="chevron" aria-hidden="true">📊</span>
										📊 Activity Dashboard
									</div>
									<div class="widget-actions">
										<a class="btn small dashboard-launch" href="#/admin/activity-dashboard" target="_blank" rel="noopener noreferrer">Open full page ↗</a>
										<button type="button" class="btn small" on:click={() => closeWidget('activity')}>Close</button>
									</div>
								</div>
								<div class="widget-body activity-launch-body" id="adminall-widget-activity-body">
									<p>The operations market dashboard now opens in its own full-width browser tab.</p>
									<a class="dashboard-launch large" href="#/admin/activity-dashboard" target="_blank" rel="noopener noreferrer">Launch Activity Dashboard ↗</a>
								</div>
							</div>
						{/if}

						{#if key === 'classAttendance'}
							<div class="card widget attendance-card" id="adminall-widget-class-attendance">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-class-attendance-body"
										aria-expanded={!collapsed.classAttendance}
										on:click={() => toggleCollapsed('classAttendance')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.classAttendance ? '▸' : '▾'}</span>
										Class Attendance
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('classAttendance')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('classAttendance')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-class-attendance-body">
									{#key refresh.classAttendance}
										<AdminClassAttendanceComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'rescheduleRequests'}
							<div class="card widget reschedule-card" id="adminall-widget-reschedule">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-reschedule-body"
										aria-expanded={!collapsed.rescheduleRequests}
										on:click={() => toggleCollapsed('rescheduleRequests')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.rescheduleRequests ? '▸' : '▾'}</span>
										Reschedule Requests
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('rescheduleRequests')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('rescheduleRequests')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-reschedule-body">
									{#key refresh.rescheduleRequests}
										<AdminRescheduleRequestsComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'classRegistrationRequests'}
							<div class="card widget registration-requests-card" id="adminall-widget-class-registration-requests">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-class-registration-requests-body"
										aria-expanded={!collapsed.classRegistrationRequests}
										on:click={() => toggleCollapsed('classRegistrationRequests')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.classRegistrationRequests ? '▸' : '▾'}</span>
										Class Registration Requests
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('classRegistrationRequests')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('classRegistrationRequests')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-class-registration-requests-body">
									{#key refresh.classRegistrationRequests}
										<AdminClassRegistrationRequestsComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'classRegistrationCreate'}
							<div class="card widget registration-create-card" id="adminall-widget-class-registration-create">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-class-registration-create-body"
										aria-expanded={!collapsed.classRegistrationCreate}
										on:click={() => toggleCollapsed('classRegistrationCreate')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.classRegistrationCreate ? '▸' : '▾'}</span>
										Create Class Registration
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('classRegistrationCreate')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('classRegistrationCreate')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-class-registration-create-body">
									{#key refresh.classRegistrationCreate}
										<AdminRegistrationComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'smsRecipients'}
							<div class="card widget sms-card" id="adminall-widget-sms">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-sms-body"
										aria-expanded={!collapsed.smsRecipients}
										on:click={() => toggleCollapsed('smsRecipients')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.smsRecipients ? '▸' : '▾'}</span>
										Send Bulk SMS
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('smsRecipients')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('smsRecipients')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-sms-body">
									{#key refresh.smsRecipients}
										<SelectUsersSMSComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'informationSectionsSubmit'}
							<div class="card widget info-submit-card" id="adminall-widget-info-submit">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-info-submit-body"
										aria-expanded={!collapsed.informationSectionsSubmit}
										on:click={() => toggleCollapsed('informationSectionsSubmit')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.informationSectionsSubmit ? '▸' : '▾'}</span>
										Bendra Informacija SUBMIT
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('informationSectionsSubmit')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('informationSectionsSubmit')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-info-submit-body">
									{#key refresh.informationSectionsSubmit}
										<CreateInformationSectionComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'informationSectionsDisplay'}
							<div class="card widget info-display-card" id="adminall-widget-info-display">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-info-display-body"
										aria-expanded={!collapsed.informationSectionsDisplay}
										on:click={() => toggleCollapsed('informationSectionsDisplay')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.informationSectionsDisplay ? '▸' : '▾'}</span>
										Bendra Informacija DISPLAY
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('informationSectionsDisplay')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('informationSectionsDisplay')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-info-display-body">
									{#key refresh.informationSectionsDisplay}
										<InformationSectionsDisplayComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'teachersList'}
							<div class="card widget teachers-list-card" id="adminall-widget-teachers-list">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-teachers-list-body"
										aria-expanded={!collapsed.teachersList}
										on:click={() => toggleCollapsed('teachersList')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.teachersList ? '▸' : '▾'}</span>
										Teachers List
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('teachersList')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('teachersList')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-teachers-list-body">
									{#key refresh.teachersList}
										<TeachersListComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'studentsList'}
							<div class="card widget students-list-card" id="adminall-widget-students-list">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-students-list-body"
										aria-expanded={!collapsed.studentsList}
										on:click={() => toggleCollapsed('studentsList')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.studentsList ? '▸' : '▾'}</span>
										Students List
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('studentsList')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('studentsList')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-students-list-body">
									{#key refresh.studentsList}
										<StudentsListComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'firstClassesNewTeachers'}
							<div class="card widget first-classes-card" id="adminall-widget-first-classes">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-first-classes-body"
										aria-expanded={!collapsed.firstClassesNewTeachers}
										on:click={() => toggleCollapsed('firstClassesNewTeachers')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.firstClassesNewTeachers ? '▸' : '▾'}</span>
										First Classes with New Teachers
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('firstClassesNewTeachers')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('firstClassesNewTeachers')}>Close</button>
									</div>
								</div>
								<div class="widget-body scroll" id="adminall-widget-first-classes-body">
									{#key refresh.firstClassesNewTeachers}
										<AdminFirstClassesNewTeachersComponent />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'balanceCsv'}
							<div class="card widget balance-csv-card" id="adminall-widget-balance-csv">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-balance-csv-body"
										aria-expanded={!collapsed.balanceCsv}
										on:click={() => toggleCollapsed('balanceCsv')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.balanceCsv ? '▸' : '▾'}</span>
										📥 Balance CSV
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('balanceCsv')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('balanceCsv')}>Close</button>
									</div>
								</div>
								<div class="widget-body" id="adminall-widget-balance-csv-body">
									{#key refresh.balanceCsv}
										<BalanceTransactionsCsvDownloader />
									{/key}
								</div>
							</div>
						{/if}

						{#if key === 'teacherRevenueCsv'}
							<div class="card widget revenue-csv-card" id="adminall-widget-teacher-revenue-csv">
								<div class="widget-header">
									<button
										type="button"
										class="widget-title"
										aria-controls="adminall-widget-teacher-revenue-csv-body"
										aria-expanded={!collapsed.teacherRevenueCsv}
										on:click={() => toggleCollapsed('teacherRevenueCsv')}
									>
										<span class="chevron" aria-hidden="true">{collapsed.teacherRevenueCsv ? '▸' : '▾'}</span>
										💰 Teacher Revenue CSV
									</button>
									<div class="widget-actions">
										<button type="button" class="btn small" on:click={() => reloadWidget('teacherRevenueCsv')}>Reload</button>
										<button type="button" class="btn small" on:click={() => closeWidget('teacherRevenueCsv')}>Close</button>
									</div>
								</div>
								<div class="widget-body" id="adminall-widget-teacher-revenue-csv-body">
									{#key refresh.teacherRevenueCsv}
										<TeacherRevenuePaymentCsvDownloader />
									{/key}
								</div>
							</div>
						{/if}
					{/each}
				{/if}
			</div>
		</section>
	</div>
</div>

<style>
	.page {
		padding: 18px 22px 30px;
		background: #f6f7fb;
	}

	.section {
		margin-bottom: 22px;
	}

	.section-title {
		margin: 0 0 10px 0;
		font-size: 18px;
	}

	.section-title-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin: 0 0 10px 0;
	}

	.widgets-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 16px;
		align-items: start;
		/* Visually separate the widget area from the dock */
		padding: 14px;
		border-radius: 14px;
		background: rgba(255, 255, 255, 0.8);
		border: 1px solid rgba(0, 0, 0, 0.06);
	}

	.widgets-grid > .card {
		display: flex;
		box-sizing: border-box;
		margin: 0;
	}

	.widgets-grid > .attendance-card {
		grid-column: 1 / -1;
	}

	@supports not (color: color-mix(in srgb, red, blue)) {
		.widget-dock {
			background: #fff7ed; /* orange-50 */
			border-color: rgba(245, 158, 11, 0.25);
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
		}
		.widget-dock-label {
			color: #9a3412; /* orange-800 */
		}
		.dock-btn {
			background: #ffedd5; /* orange-100 */
			border-color: rgba(245, 158, 11, 0.35);
			color: #7c2d12; /* orange-900 */
		}
		.dock-btn:hover {
			background: #fed7aa; /* orange-200 */
			border-color: rgba(245, 158, 11, 0.55);
			box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
			transform: translateY(-1px);
		}
		.dock-btn:focus-visible {
			outline-color: rgba(245, 158, 11, 0.55);
		}
	}

	.widget-dock {
		--dock-orange: #f59e0b;
		margin: 10px 0 14px;
		padding: 14px 16px;
		border-radius: 12px;
		background:
			color-mix(in srgb, var(--dock-orange) 10%, transparent) 0 0 / 100% 100% no-repeat,
			color-mix(in srgb, Canvas 96%, var(--dock-orange) 4%);
		border: 1px solid color-mix(in srgb, var(--dock-orange) 28%, transparent);
		box-shadow: 0 1px 3px color-mix(in srgb, var(--dock-orange) 20%, transparent);
	}

	.widget-dock-label {
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: color-mix(in srgb, var(--dock-orange) 70%, #111827 30%);
		margin: 0 0 10px 0;
	}

	.widget-dock-buttons {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 10px;
		align-items: stretch;
	}

	.dock-btn {
		--dock-btn-bg: color-mix(in srgb, var(--dock-orange) 14%, transparent);
		--dock-btn-border: color-mix(in srgb, var(--dock-orange) 30%, transparent);
		--dock-btn-text: color-mix(in srgb, #111827 70%, var(--dock-orange) 30%);
		--dock-btn-shadow: color-mix(in srgb, var(--dock-orange) 25%, transparent);

		width: 100%;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		text-align: left;
		padding: 8px 10px;
		min-height: 36px;
		border: 1px solid var(--dock-btn-border);
		background: var(--dock-btn-bg);
		border-radius: 12px;
		cursor: pointer;
		font-size: 13px;
		font-weight: 600;
		color: var(--dock-btn-text);
		white-space: nowrap;
		transition:
			transform 0.15s ease,
			box-shadow 0.15s ease,
			background-color 0.15s ease,
			border-color 0.15s ease;
	}

	.dock-btn:hover {
		background: color-mix(in srgb, var(--dock-orange) 20%, transparent);
		border-color: color-mix(in srgb, var(--dock-orange) 44%, transparent);
		box-shadow: 0 2px 6px var(--dock-btn-shadow);
		transform: translateY(-1px);
	}

	.dock-btn:active {
		transform: translateY(0);
		box-shadow: 0 1px 2px var(--dock-btn-shadow);
	}

	.dock-btn:focus {
		outline: none;
	}

	.dock-btn:focus-visible {
		outline: 2px solid color-mix(in srgb, var(--dock-orange) 55%, transparent);
		outline-offset: 2px;
	}

	@media (prefers-reduced-motion: reduce) {
		.dock-btn {
			transition: none;
		}

		.dock-btn:hover {
			transform: none;
		}
	}

	@media (max-width: 1100px) {
		.widget-dock-buttons {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}

	@media (max-width: 850px) {
		.widget-dock-buttons {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}

	@media (max-width: 600px) {
		.widget-dock-buttons {
			grid-template-columns: 1fr;
		}
	}

	.widgets-empty {
		column-span: all;
	}

	.card {
		padding: 15px;
		background-color: white;
		display: flex;
		flex-direction: column;
		gap: 10px;
		box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
		border-radius: 8px;
		color: black;
		align-items: stretch;
	}

	.card.widget {
		background: var(--tint, #fff);
		border-left: 6px solid var(--accent, transparent);
	}

	.activity-card {
		--accent: #0ea5e9;
		--tint: #e0f2fe;
	}

	.activity-launch-body {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 18px;
		padding: 12px 8px 4px;
	}

	.activity-launch-body p {
		margin: 0;
		color: #475569;
	}

	.dashboard-launch {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: #0c131d;
		border: 1px solid #1b8d68;
		border-radius: 6px;
		color: #dce7f3;
		font-weight: 700;
		text-decoration: none;
		white-space: nowrap;
	}

	.dashboard-launch.large {
		padding: 10px 14px;
	}
	.reschedule-card {
		--accent: #f59e0b;
		--tint: #fffbeb;
	}
	.registration-requests-card {
		--accent: #2563eb;
		--tint: #eff6ff;
	}
	.registration-create-card {
		--accent: #7c3aed;
		--tint: #f5f3ff;
	}
	.sms-card {
		--accent: #f97316;
		--tint: #ffedd5;
	}
	.info-submit-card {
		--accent: #14b8a6;
		--tint: #ccfbf1;
	}
	.info-display-card {
		--accent: #06b6d4;
		--tint: #cffafe;
	}
	.teachers-list-card {
		--accent: #111827;
		--tint: #f3f4f6;
	}
	.students-list-card {
		--accent: #374151;
		--tint: #f9fafb;
	}
	.first-classes-card {
		--accent: #059669;
		--tint: #ecfdf5;
	}
	.balance-csv-card {
		--accent: #6b7280;
		--tint: #f3f4f6;
	}
	.revenue-csv-card {
		--accent: #dc2626;
		--tint: #fef2f2;
	}

	.widget-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
	}

	.widget-actions {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.widget-title {
		display: inline-flex;
		align-items: center;
		gap: 10px;
		padding: 6px 8px;
		border: 0;
		background: transparent;
		font-weight: 800;
		color: #1f2937;
		cursor: pointer;
		border-radius: 8px;
		text-align: left;
		/* Ensure all titles align perfectly across widgets */
		white-space: nowrap;
	}

	.widget-title:hover {
		background: rgba(255, 255, 255, 0.7);
	}

	.widget-title:focus {
		outline: 2px solid rgba(20, 105, 91, 0.4);
		outline-offset: 2px;
	}

	.chevron {
		/* Fixed width so title text starts on same x */
		width: 18px;
		display: inline-flex;
		justify-content: center;
		font-size: 14px;
		opacity: 0.9;
	}

	.widget-body {
		width: 100%;
	}

	.widget-body.scroll {
		max-height: 650px;
		overflow: auto;
		padding: 10px;
		border-radius: 8px;
		border: 1px solid rgba(0, 0, 0, 0.08);
		background: rgba(255, 255, 255, 0.75);
	}

	.btn {
		padding: 8px 12px;
		border: 1px solid #ccc;
		background: white;
		border-radius: 8px;
		cursor: pointer;
		white-space: nowrap;
	}

	.btn.small {
		padding: 6px 10px;
		border-radius: 6px;
		font-size: 12px;
	}

	.empty {
		padding: 18px 16px;
		border-radius: 8px;
		background: #f8f9fa;
		border: 1px solid #e0e0e0;
		color: #666;
	}

	@media (max-width: 1100px) {
		.widgets-grid {
			grid-template-columns: 1fr;
		}
	}

	@media (max-width: 600px) {
		.activity-launch-body {
			align-items: stretch;
			flex-direction: column;
		}
	}
</style>

